import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Lock, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../mockData';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Services', path: '/services' },
      { name: 'Industries', path: '/industries' }
    ],
    forYou: [
      { name: 'Employers', path: '/employers' },
      { name: 'Candidates', path: '/candidates' },
      { name: 'Contact', path: '/contact' }
    ]
  };

  return (
    <footer className="bg-[#0A1F44] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_talent-delivery-2/artifacts/n21hqg7x_1000003920%20%281%29.png" 
              alt="Omnia Paratus" 
              className="h-16 w-auto mb-4"
            />
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For You Links */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider mb-4">
              For You
            </h3>
            <ul className="space-y-3">
              {footerLinks.forYou.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider mb-4">
              Contact
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-sm text-gray-300">
                <Mail className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="hover:text-[#D4AF37] transition-colors duration-200"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3 text-sm text-gray-300">
                <Phone className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>{companyInfo.phone}</span>
              </li>
              <li className="flex items-start space-x-3 text-sm text-gray-300">
                <MapPin className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>{companyInfo.serviceArea}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} Omnia Paratus Talent Solutions. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link 
                to="/privacy-policy" 
                className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-200"
              >
                Terms of Service
              </Link>
              <Link 
                to="/backoffice/login" 
                className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-200 flex items-center gap-1"
                data-testid="staff-login-link"
              >
                <Lock className="h-3.5 w-3.5" />
                Staff Login
              </Link>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/omnia-paratus-talent-solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#D4AF37] transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href="https://www.facebook.com/omniaparatustalentsolutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#D4AF37] transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com/omniaparatustalent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#D4AF37] transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="http://tiktok.com/@omniaparatusalent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#D4AF37] transition-colors duration-200"
                  aria-label="TikTok"
                  data-testid="tiktok-link"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 005.58 2.2V12a4.85 4.85 0 01-2.4-.64 4.83 4.83 0 01-1.6-1.67V6.69z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
